/**
 * Comprimir el cuadrante antes de mandarlo.
 *
 * No es una optimización: es lo que hace posible la ejecución asíncrona. Una invocación
 * asíncrona de Lambda admite **1 MB**, y el cuadrante del piloto —232 empleados, 3 turnos,
 * 10 tareas, 4 semanas— pesa **3,37 MB** en JSON. Comprimido se queda en 0,16, y en 0,21
 * después de que API Gateway lo entregue en base64. El 20× no es suerte: el árbol de poda es
 * el mismo puñado de identificadores repetido en decenas de miles de hojas.
 *
 * Quien lo descomprime es **TURNOS**, al final del recorrido. BACK solo lo recoge y lo
 * reenvía sin abrirlo, así que los bytes que salen de aquí tienen que llegar intactos.
 */

/**
 * ⚠️ **Sin `Content-Encoding`, a propósito.**
 *
 * Declarar `Content-Encoding: gzip` sería lo semánticamente correcto si el receptor fuera a
 * decodificarlo, y aquí no lo es: BACK reenvía los bytes tal cual. Además rompería por dos
 * sitios a la vez, y ninguno menciona la compresión en su error:
 *
 * 1. `Content-Encoding` **no está en `allowedHeaders`** del CORS de BACK (`serverless.yml`),
 *    así que el navegador tumbaría la petición en el preflight.
 * 2. Cualquier intermediario que se tome el header en serio —CloudFront, un proxy— podría
 *    descomprimir por su cuenta, y entonces TURNOS recibiría JSON plano y fallaría al hacer
 *    `gunzip`, con los dos extremos desincronizados y nadie diciendo por qué.
 *
 * Un cuerpo opaco no invita a nadie a tocarlo. Y el comodín de `binaryMediaTypes` de BACK ya
 * garantiza que API Gateway lo entregue en base64 sea cual sea el tipo.
 */
export const SOLVE_BODY_CONTENT_TYPE = 'application/octet-stream';

/**
 * El JSON de `value`, comprimido con gzip.
 *
 * Se devuelve `Blob` y no `ArrayBuffer` porque es lo que `HttpClient` manda sin tocar; un
 * objeto normal lo serializaría otra vez a JSON y desharía todo esto.
 */
export async function gzipJson(value: unknown): Promise<Blob> {
  return gzipText(JSON.stringify(value));
}

/**
 * Lo mismo, pero partiendo del JSON **ya serializado**.
 *
 * ⚠️ **Existe para no serializar dos veces, y eso costaba 23 ms de nada.** El interceptor de
 * compresión tiene que serializar igualmente para saber si el cuerpo pasa del umbral; llamando
 * después a `gzipJson` se recorría el objeto entero una segunda vez. Medido sobre un cuerpo real
 * de 7,9 MB: 12 ms el primer `stringify`, **23 el segundo**, 33 el gzip. El recorrido de más era
 * casi tanto como comprimir, y en el hilo de la interfaz.
 */
export async function gzipText(json: string): Promise<Blob> {
  return gzip(new Blob([json]));
}

/**
 * La vuelta. **Solo se usa en pruebas**: en producción quien descomprime es TURNOS.
 *
 * Existe para poder fijar que el viaje de ida y vuelta devuelve el mismo objeto, que es la
 * única garantía de que el solver recibe lo mismo que recibía antes de comprimir.
 */
export async function gunzipJson<T = unknown>(data: Blob): Promise<T> {
  return JSON.parse(await gunzip(data)) as T;
}

function gzip(source: Blob): Promise<Blob> {
  return new Response(pipe(source, new CompressionStream('gzip'))).blob();
}

function gunzip(source: Blob): Promise<string> {
  return new Response(pipe(source, new DecompressionStream('gzip'))).text();
}

function pipe(source: Blob, transform: GenericTransformStream): ReadableStream<Uint8Array> {
  return source.stream().pipeThrough(transform) as ReadableStream<Uint8Array>;
}
